/**
 * ai/handStrength.ts —— 模糊牌力分档（PRD 2.1「决策输入」）
 *
 * ⚠ 信息隔离硬约束（PRD 2.1 禁止项 / AC2.5）：
 *   本文件**严禁** import equity.ts 或 preflopTable.ts，严禁任何形式的胜率 / EV 精算。
 *   这里只做「由手牌与公共牌的结构特征启发式判定」的模糊分档 —— 也就是真人牌手脑子里
 *   那种「这牌挺强 / 这牌是边缘牌」的粗判，而不是一个精确概率。
 *   src/core/ai/__tests__/isolation.test.ts 会做依赖扫描来锁死这条约束。
 *
 * 两条链路：
 *   翻牌前：Chen formula（1990 年代 Bill Chen 提出的手动估牌力口诀）——纯结构启发式，
 *           输入只有两张底牌的点数、是否同花、间隔。再用「全部 1,326 种底牌组合的
 *           Chen 分数分布」把它换算成百分位，便于按 VPIP 目标直接卡阈值。
 *   翻牌后：成手牌型（复用 7 张评估器，这是牌型识别而非概率计算）+ 底牌参与度 +
 *           听牌结构（同花听牌 / 两头顺 / 卡顺），映射到 5 档。
 */

import { rankOf, suitOf, type Card } from '../cards';
import { bestFiveCards, categoryOf, evaluate, keyRankOf, HandCategory } from '../evaluator';

/** 模糊牌力分档（PRD 2.1）。 */
export type StrengthTier = 'monster' | 'strong' | 'medium' | 'marginal' | 'weak';

/** 分档的中文名，用于决策日志与 HUD。 */
export const TIER_NAMES_CN: Readonly<Record<StrengthTier, string>> = {
  monster: '超强',
  strong: '强',
  medium: '中等',
  marginal: '边缘',
  weak: '弱',
};

/** 牌力评估结果。 */
export interface FuzzyStrength {
  readonly tier: StrengthTier;
  /** [0,1] 的模糊强度标量。翻牌前为 Chen 分数百分位，翻牌后为结构化估分。 */
  readonly score: number;
  /** 人类可读的判定依据，写入决策日志便于复盘 AI 为什么这么打。 */
  readonly reason: string;
  /** 是否持有有价值的听牌（影响半诈唬意愿）。 */
  readonly hasDraw: boolean;
  /**
   * Round23 · FR-5：**单挑（对 1 名对手）粗略胜率近似** ∈ [0,1]。
   * 由「成手等级 + 听牌 outs（rule of 2/4 近似）+ 相对 board 的结构估分」推出，**不是**赢率精算
   * （信息隔离 AC2.5：不枚举对手手牌 / 未来公共牌）。供 postflop 概率化诈唬门槛、赔率驱动跟注、
   * 大额/全下护栏做「像人一样的粗判」。多人底池由 decision 层按在场人数再折算。
   */
  readonly equity: number;
  /** 听牌 outs 的近似张数（同花 9 / 两头顺 8 / 卡顺 4，重叠封顶 15）；无听牌为 0。 */
  readonly outs: number;
}

/** 数值夹取到 [lo,hi]。 */
function clamp01(v: number, lo = 0, hi = 1): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Round23 · FR-5：翻牌后单挑粗略胜率近似。
 *
 * 输入：结构化成手估分 `score`（postflopStrength 已算，随 board texture 单调）、成手类别 `cat`、
 * 听牌结构 `draws`、剩余待发公共牌街数。输出 [0,1] 的胜率近似：
 *   - 成手（中等以上、非纯高牌）：以 `score` 派生的成手胜率为主，叠加听牌 outs 的补充权益；
 *   - 高牌 / 听牌 / 打公共牌：`score` 本身已反映听牌价值，取「成手映射」与「outs 权益」的较大者，
 *     避免把「高牌 + 听牌」的听牌价值重复计入。
 * outs→权益用扑克经典「rule of 2 and 4」的保守版：两街 ~3.8%/out、一街 ~2.1%/out。
 */
function estimateEquity(
  score: number,
  cat: HandCategory,
  draws: DrawInfo,
  boardLen: number,
): { equity: number; outs: number } {
  const streetsLeft = boardLen >= 5 ? 0 : boardLen === 4 ? 1 : 2;
  let outs = 0;
  if (draws.flushDraw) outs += 9;
  if (draws.openEnded) outs += 8;
  else if (draws.gutshot) outs += 4;
  outs = Math.min(15, outs);
  const drawEq =
    streetsLeft === 2 ? Math.min(0.54, outs * 0.038) : streetsLeft === 1 ? Math.min(0.32, outs * 0.021) : 0;

  // 成手映射（与 score 单调，air≈0.10、顶对≈0.66、成套≈0.83、超强≈0.97）。
  const madeEq = clamp01(score * 0.96 + 0.02, 0.05, 0.98);
  // 「有真实成手」= 中等以上且非纯高牌（顶对/超对/两对/三条/顺子以上）。
  const hasMadeHand = score >= 0.45 && cat !== HandCategory.HighCard;
  let equity: number;
  if (hasMadeHand) {
    equity = Math.min(0.98, madeEq + drawEq * (1 - madeEq));
  } else {
    // 高牌 / 弱对 / 听牌：score 已含听牌价值，取成手映射与 outs 权益较大者（不重复计入）。
    equity = Math.max(madeEq, drawEq * 0.85 + 0.06);
  }
  return { equity: clamp01(equity), outs };
}

/* ------------------------------------------------------------------ *
 * 翻牌前：Chen formula
 * ------------------------------------------------------------------ */

/** Chen formula 的高牌基础分。 */
function chenHighCardPoints(rank: number): number {
  switch (rank) {
    case 12: return 10;   // A
    case 11: return 8;    // K
    case 10: return 7;    // Q
    case 9: return 6;     // J
    default: return (rank + 2) / 2; // T=5, 9=4.5, ... 2=1
  }
}

/**
 * Chen formula 原始分。
 * 规则：高牌分 → 对子×2（下限 5）→ 同花 +2 → 按间隔扣分 → 小连张 +1 → 向上取整。
 */
export function chenScore(a: Card, b: Card): number {
  const ra = rankOf(a);
  const rb = rankOf(b);
  const hi = Math.max(ra, rb);
  const lo = Math.min(ra, rb);
  const suited = suitOf(a) === suitOf(b);

  let pts: number;
  if (ra === rb) {
    pts = Math.max(5, chenHighCardPoints(hi) * 2);
  } else {
    pts = chenHighCardPoints(hi);
    if (suited) pts += 2;
    const gap = hi - lo - 1;
    if (gap === 1) pts -= 1;
    else if (gap === 2) pts -= 2;
    else if (gap === 3) pts -= 4;
    else if (gap >= 4) pts -= 5;
    // 小连张 / 小单张隔张的顺子潜力补偿（两张都小于 Q）
    if (gap <= 1 && hi < 10) pts += 1;
  }
  return Math.ceil(pts);
}

/**
 * 全部 C(52,2) = 1,326 种底牌组合的 Chen 分数分布，用于把分数换算成百分位。
 *
 * 为什么要百分位：风格参数是以 VPIP（入池率）这种「百分比」定义的，
 * 用百分位卡阈值可以让「入池阈值 = 1 − VPIP 目标」这条关系直接成立，
 * 调参时不必反复试错分数绝对值。
 */
const CHEN_DISTRIBUTION: Readonly<{ sorted: Int8Array; total: number }> = (() => {
  const scores: number[] = [];
  for (let a = 0; a < 52; a++) {
    for (let b = a + 1; b < 52; b++) scores.push(chenScore(a, b));
  }
  scores.sort((x, y) => x - y);
  return { sorted: Int8Array.from(scores), total: scores.length };
})();

/**
 * 翻牌前牌力百分位 ∈ [0,1]：表示「这手牌强于随机一手牌的比例」。
 *
 * 注意：这是**Chen 分数**的百分位，不是赢率百分位。两者高度相关但并不相同 ——
 * 这正是我们想要的「像人一样凭口诀估牌」，而不是精算。
 */
export function preflopPercentile(a: Card, b: Card): number {
  const s = chenScore(a, b);
  const arr = CHEN_DISTRIBUTION.sorted;
  // 严格小于 s 的数量 + 等于 s 的一半，避免大量同分手牌全部挤在同一个阈值边界上
  let less = 0;
  let equal = 0;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] < s) less++;
    else if (arr[i] === s) equal++;
    else break;
  }
  return (less + equal / 2) / CHEN_DISTRIBUTION.total;
}

/** 翻牌前模糊分档。 */
export function preflopStrength(hole: readonly [Card, Card]): FuzzyStrength {
  const p = preflopPercentile(hole[0], hole[1]);
  const tier: StrengthTier =
    p >= 0.94 ? 'monster' : p >= 0.82 ? 'strong' : p >= 0.6 ? 'medium' : p >= 0.35 ? 'marginal' : 'weak';
  // 翻牌前粗略胜率近似（供内部一致性；翻前决策仍走 Chen 百分位阈值，不消费此值）。
  const equity = clamp01(0.32 + 0.42 * (p - 0.5) + 0.15 * p);
  return {
    tier,
    score: p,
    reason: `翻牌前 Chen=${chenScore(hole[0], hole[1])} 百分位=${(p * 100).toFixed(0)}%`,
    hasDraw: false,
    equity,
    outs: 0,
  };
}

/* ------------------------------------------------------------------ *
 * 翻牌后：成手结构 + 听牌结构
 * ------------------------------------------------------------------ */

interface DrawInfo {
  readonly flushDraw: boolean;
  readonly openEnded: boolean;
  readonly gutshot: boolean;
}

/** 听牌结构识别（只看已公开的牌，绝不看未开牌）。 */
function detectDraws(hole: readonly [Card, Card], board: readonly Card[]): DrawInfo {
  // 河牌圈已无后续公共牌，任何「听牌」都没有兑现机会，必须一律判为无听牌。
  // 否则 AI 会在河牌拿着 4 张同花去做半诈唬，且 score 被 +0.08 的「强听牌」加成虚高。
  if (board.length >= 5) return { flushDraw: false, openEnded: false, gutshot: false };

  const all = [...hole, ...board];
  const suitCount = [0, 0, 0, 0];
  for (const c of all) suitCount[suitOf(c)]++;
  const flushDraw = suitCount.some((n) => n === 4);

  let rankMask = 0;
  for (const c of all) rankMask |= 1 << rankOf(c);
  const present = (r: number): boolean => (rankMask & (1 << r)) !== 0;

  let openEnded = false;
  let gutshot = false;
  // 遍历所有长度 5 的点数窗口，统计命中张数：
  // 命中 4 张且缺口在两端 → 两头顺听牌；缺口在中间 → 卡顺听牌。
  for (let start = -1; start <= 8; start++) {
    // start = -1 表示 A2345（A 记为 rank 12）
    const ranks = start === -1 ? [12, 0, 1, 2, 3] : [start, start + 1, start + 2, start + 3, start + 4];
    let hit = 0;
    let missIndex = -1;
    for (let i = 0; i < 5; i++) {
      if (present(ranks[i])) hit++;
      else missIndex = i;
    }
    if (hit === 4) {
      // 缺的那张在两端 → 更接近两头顺；缺在中间 → 卡顺
      if (missIndex === 0 || missIndex === 4) openEnded = true;
      else gutshot = true;
    }
  }
  return { flushDraw, openEnded, gutshot };
}

/** 翻牌后模糊分档。 */
export function postflopStrength(hole: readonly [Card, Card], board: readonly Card[]): FuzzyStrength {
  const all = [...hole, ...board];
  const value = evaluate(all);
  const cat = categoryOf(value);
  const best5 = new Set(bestFiveCards(all));
  const holeUsed = (best5.has(hole[0]) ? 1 : 0) + (best5.has(hole[1]) ? 1 : 0);
  const draws = detectDraws(hole, board);
  const hasDraw = draws.flushDraw || draws.openEnded;

  const boardMaxRank = board.reduce((m, c) => Math.max(m, rankOf(c)), -1);
  const holeMaxRank = Math.max(rankOf(hole[0]), rankOf(hole[1]));

  let score: number;
  let reason: string;

  switch (cat) {
    case HandCategory.StraightFlush:
    case HandCategory.FourOfAKind:
      score = 0.99; reason = '成超强牌（同花顺 / 四条）'; break;

    case HandCategory.FullHouse:
      score = holeUsed > 0 ? 0.96 : 0.8;
      reason = `葫芦（底牌参与 ${holeUsed} 张）`; break;

    case HandCategory.Flush:
      score = holeUsed > 0 ? 0.9 : 0.62;
      reason = `同花（底牌参与 ${holeUsed} 张）`; break;

    case HandCategory.Straight:
      score = holeUsed > 0 ? 0.86 : 0.58;
      reason = `顺子（底牌参与 ${holeUsed} 张）`; break;

    case HandCategory.ThreeOfAKind:
      score = holeUsed === 2 ? 0.84 : holeUsed === 1 ? 0.78 : 0.5;
      reason = `三条（底牌参与 ${holeUsed} 张）`; break;

    case HandCategory.TwoPair:
      score = holeUsed === 2 ? 0.72 : holeUsed === 1 ? 0.56 : 0.34;
      reason = `两对（底牌参与 ${holeUsed} 张）`; break;

    case HandCategory.Pair: {
      const pairRank = keyRankOf(value, 0);
      const pocketPair = rankOf(hole[0]) === rankOf(hole[1]);
      if (holeUsed === 0) {
        // 公共牌自带的对子，我没参与 —— 实际只是高牌
        score = draws.flushDraw ? 0.42 : draws.openEnded ? 0.38 : 0.2;
        reason = '公共牌对子，底牌未参与（等同高牌）';
      } else if (pocketPair && pairRank > boardMaxRank) {
        score = 0.75; reason = '超对（口袋对大于公共牌最大点）';
      } else if (pairRank === boardMaxRank) {
        score = 0.68; reason = '顶对';
      } else if (pairRank > board.reduce((m, c) => Math.min(m, rankOf(c)), 99)) {
        score = 0.5; reason = '中对';
      } else {
        score = 0.36; reason = '底对';
      }
      if (draws.flushDraw || draws.openEnded) { score = Math.min(0.82, score + 0.08); reason += ' + 强听牌'; }
      break;
    }

    default: {
      // 高牌
      if (draws.flushDraw && draws.openEnded) { score = 0.5; reason = '高牌 + 同花听牌 + 两头顺'; }
      else if (draws.flushDraw) { score = 0.4; reason = '高牌 + 同花听牌'; }
      else if (draws.openEnded) { score = 0.36; reason = '高牌 + 两头顺听牌'; }
      else if (draws.gutshot) { score = 0.24; reason = '高牌 + 卡顺听牌'; }
      else if (holeMaxRank === 12) { score = 0.18; reason = 'A 高牌，无听牌'; }
      else { score = 0.08; reason = '无成手、无听牌'; }
      break;
    }
  }

  const tier: StrengthTier =
    score >= 0.85 ? 'monster' : score >= 0.65 ? 'strong' : score >= 0.45 ? 'medium' : score >= 0.28 ? 'marginal' : 'weak';
  const { equity, outs } = estimateEquity(score, cat, draws, board.length);
  return { tier, score, reason, hasDraw, equity, outs };
}

/** 统一入口：按已公开公共牌数量自动选择翻牌前 / 翻牌后链路。 */
export function fuzzyStrength(hole: readonly [Card, Card], board: readonly Card[]): FuzzyStrength {
  return board.length === 0 ? preflopStrength(hole) : postflopStrength(hole, board);
}
